import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Backdrop,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  login,
  storeTokens,
  getUserFromToken,
  getKivoSignInUrl,
  getUserPermissions,
  clearTokens,
  LOGOUT_REASON_STORAGE_KEY,
} from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { isKivoSignInEnabled } from '../utils/kivoFeature';
import {
  getKioskHomePath,
  isKioskMode,
  isKioskSupportedUser,
} from '../utils/kioskMode';

const Login = () => {
  const navigate = useNavigate();
  const { login: loginContext } = useAuth();
  const kivoSignInEnabled = isKivoSignInEnabled();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isKivoLoading, setIsKivoLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');

  useEffect(() => {
    const storedReason = sessionStorage.getItem(LOGOUT_REASON_STORAGE_KEY);
    if (!storedReason) return;

    sessionStorage.removeItem(LOGOUT_REASON_STORAGE_KEY);
    try {
      const parsed = JSON.parse(storedReason);
      if (parsed?.reason === 'ACCOUNT_INACTIVE') {
        setSessionMessage(
          parsed.message ||
          'Your account has been deactivated. Please contact your administrator.'
        );
      } else if (parsed?.reason === 'SESSION_REVOKED') {
        setSessionMessage(
          parsed.message ||
          'Your session was ended by an administrator. Please sign in again.'
        );
      } else {
        setSessionMessage(
          parsed?.message ||
          'Your session has expired. Please sign in again.'
        );
      }
    } catch (_) {
      setSessionMessage('Your session has ended. Please sign in again.');
    }

    const hideTimer = setTimeout(() => setSessionMessage(''), 4000);
    return () => clearTimeout(hideTimer);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({}); // Clear any previous errors
    
    try {
      const data = await login(formData.email, formData.password);
      
      // Store tokens and user info
      if (data.access_token) {
        // console.log("access_token:", data.access_token);
        storeTokens(data.access_token, data.refresh_token);
        
        // Store user info if provided; otherwise derive from access token
        let userData = null;
        if (data.user) {
          userData = data.user;
          localStorage.setItem('user', JSON.stringify(data.user));
        } else {
          const derived = getUserFromToken();
          if (derived) {
            userData = derived;
            localStorage.setItem('user', JSON.stringify(derived));
          }
        }
        
        // console.log('Login successful:', data);
        // console.log('User data:', userData);
        
        // Show redirecting loader
        setIsLoading(false);
        setIsRedirecting(true);
        
        // Show success toast with username
        setSuccessMessage(`Signed in successfully.`);
        setShowSuccessToast(true);
        
        // Fetch permissions from backend to determine redirect path
        try {
          const permissions = await getUserPermissions();

          if (isKioskMode() && !isKioskSupportedUser(permissions)) {
            clearTokens();
            setIsLoading(false);
            setIsRedirecting(false);
            setShowSuccessToast(false);
            setErrors({
              general: 'This kiosk supports admin or intrusion accounts only.',
            });
            return;
          }

          // Update AuthContext with user data and permissions
          loginContext(userData, permissions);

          const redirectPath = isKioskMode()
            ? getKioskHomePath(permissions)
            : permissions.dashboard_path || '/dashboard';
          // console.log('Redirecting to:', redirectPath, 'based on backend permissions');
          
          // Redirect after a short delay
          setTimeout(() => {
            navigate(redirectPath);
          }, 1000);
        } catch (error) {
          console.error('Failed to fetch permissions, using default redirect:', error);
          // Still update context with user data
          loginContext(userData);
          // Fallback to default dashboard if permissions fetch fails
          setTimeout(() => {
            navigate(isKioskMode() ? '/webview' : '/dashboard');
          }, 1000);
        }
       
      } else {
        throw new Error('No access token received');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ 
        general: error.message.includes('Failed to fetch') 
          ? 'Unable to connect to server. Please check if the backend is running.' 
          : error.message || 'Login failed. Please check your credentials and try again.' 
      });
      setIsLoading(false);
      setIsRedirecting(false);
      setShowSuccessToast(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccessToast(false);
  };

  const handleKivoLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsKivoLoading(true);
    setErrors({});
    
    try {
      // Get the authorization URL from backend
      const response = await getKivoSignInUrl();
      
      if (response.authorization_url) {
        // Redirect to Kivo OAuth authorization page
        window.location.href = response.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Kivo OAuth error:', error);
      setErrors({
        general: error.message || 'Failed to initiate Kivo OAuth login. Please try again.'
      });
      setIsKivoLoading(false);
    }
  };

  return (
    <>
      {/* Success Toast Notification */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, mt: 4 }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '1rem',
            fontWeight: 'bold',
            color:'#40518A',
            backgroundColor: '#f5f5f5',
            
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop after successful login */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isRedirecting}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Redirecting to Dashboard...
          </Typography>
        </Box>
      </Backdrop>

      <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography component="h1" variant="h4" gutterBottom>
                Kivo Face Detection
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to your account
              </Typography>
            </Box>

            {errors.general && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.general}
              </Alert>
            )}

            {sessionMessage && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {sessionMessage}
              </Alert>
            )}

            {errors.success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {errors.success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>

            {kivoSignInEnabled && (
              <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Button
              type="button"
              fullWidth
              variant="outlined"
              color="primary"
              sx={{ mb: 2, py: 1.5 }}
              disabled={isKivoLoading}
              onClick={handleKivoLogin}
            >
              {isKivoLoading ? 'Redirecting to Kivo...' : 'Sign In with Kivo'}
            </Button>
              </>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Button
                  variant="text"
                  color="primary"
                  sx={{ textTransform: 'none' }}
                  onClick={() => console.log('Navigate to sign up')}
                >
                  Sign up here
                </Button>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      </Container>
    </>
  );
};

export default Login;
